<template>
  <div class="faraday-induction-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ε = -N (ΔΦ/Δt)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220" @mousemove="dragMagnet" @mouseup="stopDrag" @mouseleave="stopDrag" @touchmove.prevent="dragMagnetTouch" @touchend="stopDrag">
          <!-- Real-time Chart -->
          <g transform="translate(10, 10)">
             <rect x="0" y="0" width="120" height="90" fill="#0f172a" stroke="#334155" />
             <line x1="0" y1="45" x2="120" y2="45" stroke="#475569" stroke-width="1" />
             <text x="5" y="12" fill="#94a3b8" font-size="9">磁通量 Φ</text>
             <text x="5" y="85" fill="#fcd34d" font-size="9">感应电动势 ε</text>
             
             <!-- Chart paths -->
             <path :d="fluxPath" fill="none" stroke="#94a3b8" stroke-width="1.5" />
             <path :d="emfPath" fill="none" stroke="#fcd34d" stroke-width="2" />
          </g>

          <!-- Solenoid Coil -->
          <g transform="translate(250, 100)">
             <!-- Back side of coils -->
             <path d="M -30,-40 Q -60,-40 -60,0 Q -60,40 -30,40" fill="none" stroke="#b87333" stroke-width="4" opacity="0.3" />
             <path d="M -10,-40 Q -40,-40 -40,0 Q -40,40 -10,40" fill="none" stroke="#b87333" stroke-width="4" opacity="0.3" />
             <path d="M 10,-40 Q -20,-40 -20,0 Q -20,40 10,40" fill="none" stroke="#b87333" stroke-width="4" opacity="0.3" />
             <path d="M 30,-40 Q 0,-40 0,0 Q 0,40 30,40" fill="none" stroke="#b87333" stroke-width="4" opacity="0.3" />
          </g>

          <!-- Magnet Graphic -->
          <g :transform="`translate(${magnetX}, 100)`" style="cursor: grab;" @mousedown="startDrag" @touchstart.prevent="startDragTouch">
             <!-- Magnetic Field Lines (Fading out) -->
             <g stroke="rgba(255, 255, 255, 0.2)" stroke-width="1.5" fill="none">
                <path d="M 40, -10 Q 80, -40 100, 0 Q 80, 40 40, 10" />
                <path d="M 40, -15 Q 100, -60 130, 0 Q 100, 60 40, 15" />
             </g>
             
             <!-- Bar Magnet -->
             <rect x="-40" y="-15" width="40" height="30" fill="#3b82f6" rx="2" />
             <rect x="0" y="-15" width="40" height="30" fill="#ef4444" rx="2" />
             <text x="-25" y="4" fill="#f8fafc" font-size="12" font-weight="bold">S</text>
             <text x="15" y="4" fill="#f8fafc" font-size="12" font-weight="bold">N</text>
          </g>

          <!-- Solenoid Coil Front side -->
          <g transform="translate(250, 100)">
             <path d="M -30,-40 Q 0,-40 0,0 Q 0,40 -30,40" fill="none" stroke="#b87333" stroke-width="4" />
             <path d="M -10,-40 Q 20,-40 20,0 Q 20,40 -10,40" fill="none" stroke="#b87333" stroke-width="4" />
             <path d="M 10,-40 Q 40,-40 40,0 Q 40,40 10,40" fill="none" stroke="#b87333" stroke-width="4" />
             <path d="M 30,-40 Q 60,-40 60,0 Q 60,40 30,40" fill="none" stroke="#b87333" stroke-width="4" />
             
             <!-- Glow effect if EMF is high -->
             <circle cx="15" cy="0" r="45" fill="none" :stroke="emf > 0 ? '#ef4444' : '#0ea5e9'" stroke-width="8" :opacity="Math.min(Math.abs(emf)*0.05, 0.5)" filter="blur(4px)" />
          </g>

          <!-- Galvanometer Base -->
          <g transform="translate(300, 200)">
             <!-- Wires from coil to galvanometer -->
             <path d="M -80,-60 L -80,-10 L -20,-10" fill="none" stroke="#94a3b8" stroke-width="2" />
             <path d="M -20,-60 L 20,-60 L 20,-10" fill="none" stroke="#94a3b8" stroke-width="2" />
             
             <rect x="-25" y="-20" width="50" height="25" fill="#1e293b" stroke="#475569" stroke-width="2" rx="4" />
             <line x1="0" y1="5" :x2="needle.x" :y2="needle.y" stroke="#fcd34d" stroke-width="2" stroke-linecap="round" />
             <circle cx="0" cy="5" r="3" fill="#cbd5e1" />
             <text x="0" y="-8" text-anchor="middle" fill="#94a3b8" font-size="8">G (检流计)</text>
          </g>

          <text v-if="dragging" :x="magnetX" y="60" fill="#fff" font-size="10" text-anchor="middle" opacity="0.5">拖拽中...</text>
        </svg>

        <div class="hud-panel" style="right:12px; left:auto; top:12px;">
           <div class="hud-item cyan" style="text-align:right;">
              <span class="val">{{ flux.toFixed(2) }} <small>Wb</small></span>
              <label>磁通量 Φ</label>
           </div>
           <div class="hud-item orange" style="text-align:right;">
              <span class="val">{{ emf.toFixed(2) }} <small>V</small></span>
              <label>电动势 ε</label>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <el-button :type="autoDrive ? 'danger' : 'success'" size="small" plain @click="autoDrive = !autoDrive" class="w-full" icon="el-icon-sort">
                      {{ autoDrive ? '停止自动振荡' : '自动简谐振荡磁铁' }}
                    </el-button>
                 </div>
                 <div class="flex-1">
                    <p style="font-size:11px;color:#64748b;margin:0;">* 您也可以直接在上方画面中<strong>按住磁铁拖拽</strong>测试感应电流的急剧变化。</p>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>法拉第电磁感应定律指出，闭合电路中的感应电动势 $ε$ 等于穿过电路的磁通量 $Φ$ 的负变化率。在这里，磁通量随磁铁靠近线圈中心呈高斯函数分布。当您缓慢移动磁铁时，$Φ$ 变化慢，测得 $ε$ 微弱；当您<strong>快速猛拉</strong>磁铁时，瞬间极高的 $-ΔΦ/Δt$ 会产生巨大的感应电压峰值（左上角图线清晰可见导数关系：电动势即磁通曲线的负斜率）。这证实了“动磁生电”的核心奥秘。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FaradayInductionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      magnetX: 80,
      prevX: 80,
      flux: 0,
      emf: 0,
      dragging: false,
      autoDrive: true,
      time: 0,
      timer: null,
      history: []
    }
  },
  computed: {
    needle() {
       let maxEmf = 20;
       let ratio = this.emf / maxEmf;
       if (ratio > 1) ratio = 1;
       if (ratio < -1) ratio = -1;
       let angle = -Math.PI/2 + (ratio * Math.PI/3);
       return {
          x: Math.cos(angle) * 15,
          y: Math.sin(angle) * 15
       };
    },
    fluxPath() {
       return this.generatePath(h => 45 - h.flux * 35);
    },
    emfPath() {
       return this.generatePath(h => 45 - h.emf * 1.5);
    },
  },
  methods: {
    generatePath(mapper) {
       if (!this.history || this.history.length === 0) return "";
       const pts = this.history.map((h, i) => `${i},${mapper(h)}`).filter(p => !p.includes('NaN'));
       if (pts.length < 2) return "";
       return "M " + pts.join(" L ");
    },
    updatePhysics() {
       // Magnetic flux matches a gaussian envelope depending on distance to coil center (X=265)
       // Let's say coil is at X=265, width=60
       let dx = this.magnetX - 265;
       let currentFlux = Math.exp(-Math.pow(dx / 60, 2));
       
       let dt = 1; // 1 frame
       let currentEmf = - (currentFlux - this.flux) * 100 / dt; // Negative derivative
       
       // Smoothing EMF for visual stability
       this.emf = this.emf * 0.8 + currentEmf * 0.2; 
       this.flux = currentFlux;

       // Record history for charting limit 120 width
       this.history.push({ flux: this.flux, emf: this.emf });
       if (this.history.length > 120) this.history.shift();
    },
    animate() {
       if (this.autoDrive && !this.dragging) {
          this.time += 0.05;
          // Simple harmonic motion centered at 150, amplitude 120
          this.magnetX = 150 + 120 * Math.sin(this.time);
       }
       
       this.updatePhysics();
       this.timer = requestAnimationFrame(this.animate);
    },
    startDrag(e) {
       this.dragging = true;
       this.autoDrive = false;
       this.prevClientX = e.clientX;
    },
    startDragTouch(e) {
       this.startDrag(e.touches[0]);
    },
    dragMagnet(e) {
       if(!this.dragging) return;
       let dx = e.clientX - this.prevClientX;
       this.magnetX += dx;
       if (this.magnetX < 40) this.magnetX = 40;
       if (this.magnetX > 300) this.magnetX = 300;
       this.prevClientX = e.clientX;
    },
    dragMagnetTouch(e) {
       this.dragMagnet(e.touches[0]);
    },
    stopDrag() {
       this.dragging = false;
    }
  },
  mounted() { 
     // fill initial history
     for(let i=0; i<120; i++) this.history.push({ flux:0, emf:0 });
     this.animate(); 
  },
  beforeDestroy() { if(this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.faraday-induction-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; user-select: none; }
.hud-panel { position: absolute; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-top: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-right: 3px solid #06b6d4; } &.orange { border-right: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; } .w-full { width: 100%; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
</style>
